import type { TgpuRoot } from 'typegpu';
import * as d from 'typegpu/data';
import { ATELIER_COAT_PATTERNS } from '../../shared/atelier';
import type { AtelierActorAsset, AtelierActorFrame, AtelierDraw } from '../../shared';
import { packVerts } from './meshVertex';
import { FaceParams, faceLayout } from './faceShader';
import { Frame, MAX_BONES, MeshVertex, Cloth, frameLayout, skinLayout, texLayout, clothLayout, meshLayout,
  charVertex, charFragment, clothFragment, hullVertex, hullFragment } from './shaders';

const rgb = (hex:string):number[] => [1,3,5].map(i=>parseInt(hex.slice(i,i+2),16)/255);
/** Painted tension lines belong to garments, never props, skin or accessories. */
export function clothInkKind(id:string):0|1|2 {
  if(['jerkin','robe','alb','gown','motley','tabard'].includes(id))return 1;
  if(['jerkin-skirt','robe-skirt','alb-skirt','gown-skirt','motley-skirt','tabard-front','tabard-back'].includes(id))return 2;
  return 0;
}
type Resource = {destroy():void};
interface MeshEntry { vb:any; hb:any; ib:any; refs:number }
interface TextureEntry { texture:any }
interface TextureLease { pending:Promise<TextureEntry>; refs:number }
export interface AtelierGpuActor { upload(frame:AtelierActorFrame, camera:Float32Array, eye:ArrayLike<number>, width:number,height:number,pxScale:number,painted?:boolean):void; draw(pass:GPURenderPassEncoder):{draws:number;triangles:number}; dispose():void }

/** Uses an existing engine device and render pass. No canvas, RAF or simulation. */
export class AtelierActorRenderer {
  private meshes=new Map<AtelierDraw,MeshEntry>();
  private textures=new Map<ImageBitmap,TextureLease>();
  private sampler:any;
  private character:any;
  private clothes:any;
  private hull:any;
  private disposed=false;
  constructor(private root:TgpuRoot,format:GPUTextureFormat,samples:number,depthFormat:GPUTextureFormat) {
    this.sampler=root.createSampler({magFilter:'linear',minFilter:'linear',mipmapFilter:'linear',addressModeU:'repeat',addressModeV:'repeat',maxAnisotropy:8});
    const common={attribs:meshLayout.attrib,targets:{format},depthStencil:{format:depthFormat,depthWriteEnabled:true,depthCompare:'less' as const},multisample:{count:samples}};
    this.character=root.createRenderPipeline({...common,vertex:charVertex,fragment:charFragment,primitive:{topology:'triangle-list',cullMode:'back',frontFace:'ccw'}});
    this.clothes=root.createRenderPipeline({...common,vertex:charVertex,fragment:clothFragment,primitive:{topology:'triangle-list',cullMode:'none'}});
    this.hull=root.createRenderPipeline({...common,vertex:hullVertex,fragment:hullFragment,primitive:{topology:'triangle-list',cullMode:'front',frontFace:'ccw'}});
  }
  private acquireMesh(draw:AtelierDraw):MeshEntry {
    const hit=this.meshes.get(draw);if(hit){hit.refs++;return hit;}
    const vb=this.root.createBuffer(d.disarrayOf(MeshVertex,draw.pos.length/3)).$usage('vertex');
    vb.write(packVerts(draw.pos,draw.nrm,draw.uv,draw.joints,draw.weights,draw.aux,draw.repair));
    const hb=draw.outlineNrm===draw.nrm?vb:this.root.createBuffer(d.disarrayOf(MeshVertex,draw.pos.length/3)).$usage('vertex');
    if(hb!==vb)hb.write(packVerts(draw.pos,draw.outlineNrm,draw.uv,draw.joints,draw.weights,draw.aux,draw.repair));
    const ib=this.root.createBuffer(d.arrayOf(d.u32,Math.max(3,draw.indices.length)),draw.indices.length?Array.from(draw.indices):[0,0,0]).$usage('index');
    const entry={vb,hb,ib,refs:1};this.meshes.set(draw,entry);return entry;
  }
  private async acquireTexture(bitmap:ImageBitmap):Promise<TextureEntry> {
    let lease=this.textures.get(bitmap);
    if(!lease){
      const pending=(async()=>{
        const levels=Math.floor(Math.log2(Math.max(bitmap.width,bitmap.height)))+1;
        const texture=this.root.createTexture({size:[bitmap.width,bitmap.height],format:'rgba8unorm',mipLevelCount:levels}).$usage('sampled','render');
        try {
          const gpu=this.root.unwrap(texture) as GPUTexture;
          for(let level=0;level<levels;level++) {
            const width=Math.max(1,bitmap.width>>level),height=Math.max(1,bitmap.height>>level);
            const source=level===0?bitmap:await createImageBitmap(bitmap,{resizeWidth:width,resizeHeight:height,resizeQuality:'high'});
            try{this.root.device.queue.copyExternalImageToTexture({source},{texture:gpu,mipLevel:level},[width,height]);}
            finally{if(source!==bitmap)source.close();}
          }
          if(this.disposed)throw new Error('Atelier renderer disposed during texture upload');
          return {texture};
        }catch(error){texture.destroy();throw error;}
      })();
      lease={pending,refs:0};this.textures.set(bitmap,lease);
    }
    // Reserve before awaiting: disposing another actor during this upload must
    // never retire a texture which already has an acquiring actor.
    lease.refs++;
    try{return await lease.pending;}
    catch(error){lease.refs--;if(this.textures.get(bitmap)===lease)this.textures.delete(bitmap);throw error;}
  }
  async add(asset:AtelierActorAsset):Promise<AtelierGpuActor> {
    if(this.disposed)throw new Error('Atelier renderer disposed');
    const bitmaps=[...new Set([asset.textures.body,...(asset.textures.head?[asset.textures.head]:[])])];
    const acquired:ImageBitmap[]=[];
    const textureGroups=new Map<ImageBitmap,any>();
    try{
      for(const bitmap of bitmaps){const entry=await this.acquireTexture(bitmap);acquired.push(bitmap);textureGroups.set(bitmap,this.root.createBindGroup(texLayout,{albedo:entry.texture,samp:this.sampler}));}
    }catch(error){for(const bitmap of acquired)await this.releaseTexture(bitmap);throw error;}
    if(this.disposed){for(const bitmap of acquired)this.releaseTexture(bitmap);throw new Error('Atelier renderer disposed');}
    const resources:Resource[]=[],meshRefs:AtelierDraw[]=[];
    try {
      const frameBuffer=this.root.createBuffer(Frame).$usage('uniform'),faceBuffer=this.root.createBuffer(FaceParams).$usage('uniform');
      const boneBuffer=this.root.createBuffer(d.arrayOf(d.mat4x4f,MAX_BONES)).$usage('storage');
      resources.push(frameBuffer,faceBuffer,boneBuffer);
      const frameGroup=this.root.createBindGroup(frameLayout,{frame:frameBuffer}),faceGroup=this.root.createBindGroup(faceLayout,{face:faceBuffer}),boneGroup=this.root.createBindGroup(skinLayout,{bones:boneBuffer});
      const draws=asset.draws.map(packet=>{
        const mesh=this.acquireMesh(packet);meshRefs.push(packet);
        let fill:any;
        if(packet.material==='cloth'){
          const cb=this.root.createBuffer(Cloth).$usage('uniform');resources.push(cb);const s=packet.style;
          const inkKind=clothInkKind(packet.id);let halfWidth=.06;
          if(inkKind===1)for(let i=0;i<packet.pos.length;i+=3)halfWidth=Math.max(halfWidth,Math.abs(packet.pos[i]-packet.center[0])*.62);
          cb.write(new Float32Array([...rgb(s.base),1,...rgb(s.trim),1,...rgb(s.accent),1,s.pattern,s.trimWidth,s.folds,inkKind,...packet.center.slice(0,3),inkKind===1?halfWidth:packet.center[3]]).buffer);
          fill=this.clothes.with(this.root.createBindGroup(clothLayout,{cloth:cb}));
        }else fill=this.character.with(faceGroup).with(textureGroups.get(packet.material==='head'?asset.textures.head??asset.textures.body:asset.textures.body));
        return {fill:fill.with(frameGroup).with(boneGroup).with(meshLayout,mesh.vb).withIndexBuffer(mesh.ib),
          hull:this.hull.with(frameGroup).with(boneGroup).with(meshLayout,mesh.hb).withIndexBuffer(mesh.ib),count:packet.indices.length};
      });
      const frameHost=new Float32Array(d.sizeOf(Frame)/4),bones=new Float32Array(MAX_BONES*16),faceHost=new Float32Array(d.sizeOf(FaceParams)/4);
      let disposed=false;
      return {
        upload:(frame,camera,eye,width,height,pxScale,painted=false)=>{
          if(disposed)return;
          frameHost.set(camera,0);frameHost.set(frame.model,16);frameHost.set([eye[0],eye[1],eye[2],1],32);
          frameHost.set([.55,.8,.45,0],36);frameHost.set([frame.time,pxScale,0,frame.coat?1:0],40);
          frameHost.set([width,height,1/width,1/height],44);
          const colors=frame.coat??['#000000','#808080','#ffffff'];
          for(let i=0;i<3;i++)frameHost.set([...rgb(colors[i]),1],48+i*4);
          frameHost.set([.35,.17,painted?1:0,.028],60);frameHost.set([0,-1,frame.hitFlash,1],64);
          frameHost.set([ATELIER_COAT_PATTERNS.indexOf(frame.coatPattern??'source'),...(frame.coatFit??[0,.5,.12])],68);
          frameHost.set([frame.coatSeed??0,frame.coatScale??1,0,0],72);
          bones.fill(0);for(let i=0;i<MAX_BONES;i++)bones[i*16]=bones[i*16+5]=bones[i*16+10]=bones[i*16+15]=1;
          bones.set(frame.palette.subarray(0,bones.length));faceHost.set(frame.face);boneBuffer.write(bones.buffer);faceBuffer.write(faceHost.buffer);frameBuffer.write(frameHost.buffer);
        },
        draw:pass=>{
          if(disposed)return {draws:0,triangles:0};let count=0,triangles=0;
          for(const draw of draws)if(draw.count){draw.fill.with(pass).drawIndexed(draw.count);count++;triangles+=draw.count/3;}
          for(const draw of draws)if(draw.count){draw.hull.with(pass).drawIndexed(draw.count);count++;triangles+=draw.count/3;}
          return {draws:count,triangles};
        },
        dispose:()=>{if(disposed)return;disposed=true;resources.forEach(r=>r.destroy());for(const packet of meshRefs)this.releaseMesh(packet);for(const bitmap of acquired)void this.releaseTexture(bitmap);},
      };
    }catch(error){resources.forEach(r=>r.destroy());for(const packet of meshRefs)this.releaseMesh(packet);for(const bitmap of acquired)await this.releaseTexture(bitmap);throw error;}
  }
  private releaseMesh(packet:AtelierDraw):void {
    const entry=this.meshes.get(packet);if(!entry||--entry.refs>0)return;
    entry.vb.destroy();if(entry.hb!==entry.vb)entry.hb.destroy();entry.ib.destroy();this.meshes.delete(packet);
  }
  private releaseTexture(bitmap:ImageBitmap):void {
    const lease=this.textures.get(bitmap);if(!lease||--lease.refs>0)return;
    this.textures.delete(bitmap);void lease.pending.then(entry=>entry.texture.destroy(),()=>{});
  }
  dispose():void{if(this.disposed)return;this.disposed=true;for(const entry of this.meshes.values()){entry.vb.destroy();if(entry.hb!==entry.vb)entry.hb.destroy();entry.ib.destroy();}this.meshes.clear();for(const lease of this.textures.values())void lease.pending.then(entry=>entry.texture.destroy(),()=>{});this.textures.clear();}
}
